import { DragEvent, FC, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useMutation } from "convex/react";
import { api, Id } from "@/smart-views/client/api";
import { ChippyText, hasChips } from "./chips";
import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSMenuItemVariant,
} from "@/mds";
import { CitationChip } from "../citations/CitationChip";
import { getVisibleSourceIdsForRepeatedRun } from "../citations/source-utils";

interface WorkflowSection {
  id: string;
  title: string;
}

interface TableRow {
  id: string;
  cells: string[];
  sectionId?: string;
  hidden?: boolean;
  sources?: string[];
}

type TableRowInput = string[] | TableRow;
type DropPlacement = "before" | "after";

interface TableGroup {
  section: WorkflowSection | null;
  rows: TableRow[];
  showHeader: boolean;
}

interface TableBlockProps {
  blockId: Id<"blocks">;
  columns: string[];
  rows: TableRowInput[];
  rowSources?: string[][];
  sections?: WorkflowSection[];
  isReadOnly?: boolean;
  onUserAction?: (summary: string) => void;
}

const EMPTY_ROW_SOURCES: string[][] = [];

export const TableBlock: FC<TableBlockProps> = ({
  blockId,
  columns,
  rows,
  rowSources = EMPTY_ROW_SOURCES,
  sections = [],
  isReadOnly = false,
  onUserAction,
}) => {
  const updateContent = useMutation(api.smartViews.client.updateBlockContent);
  const [draftCols, setDraftCols] = useState(columns);
  const [draftRows, setDraftRows] = useState<TableRow[]>(() => normalizeRows(rows, rowSources));
  const [draftSections, setDraftSections] = useState<WorkflowSection[]>(sections);
  const lastCommittedRowsRef = useRef<TableRow[]>(normalizeRows(rows, rowSources));
  const [activeCell, setActiveCell] = useState<{ rowId: string; col: number } | null>(null);
  const [draggedRowId, setDraggedRowId] = useState<string | null>(null);
  const [dropTargetRow, setDropTargetRow] = useState<{
    rowId: string;
    placement: DropPlacement;
  } | null>(null);
  const cellInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (activeCell !== null) cellInputRef.current?.focus();
  }, [activeCell]);

  useEffect(() => {
    const normalizedRows = normalizeRows(rows, rowSources);
    setDraftCols(columns);
    setDraftRows(normalizedRows);
    setDraftSections(sections);
    lastCommittedRowsRef.current = normalizedRows;
  }, [columns, rows, rowSources, sections]);

  const commit = (
    cols: string[] = draftCols,
    rs: TableRow[] = draftRows,
    nextSections: WorkflowSection[] = draftSections
  ) => {
    void updateContent({
      blockId,
      content: buildTableContent(cols, rs, nextSections),
    });
    lastCommittedRowsRef.current = rs;
  };

  const commitWithNotify = (cols: string[], rs: TableRow[]) => {
    const prev = lastCommittedRowsRef.current;
    commit(cols, rs);
    if (!onUserAction) return;

    for (let r = 0; r < rs.length; r++) {
      const newRow = rs[r];
      const prevRow = prev.find(row => row.id === newRow.id) ?? prev[r];
      if (!prevRow) continue;
      for (let c = 0; c < newRow.cells.length; c++) {
        if ((prevRow.cells[c] ?? "") !== (newRow.cells[c] ?? "")) {
          const colName = cols[c]?.trim() || `column ${c + 1}`;
          const rowLabel = newRow.cells[0]?.trim() || `row ${r + 1}`;
          onUserAction(`Changed "${colName}" for "${rowLabel}" to "${newRow.cells[c]}".`);
          return;
        }
      }
    }
  };

  const updateCell = (rowId: string, colIdx: number, value: string) => {
    setDraftRows(curr =>
      curr.map(row =>
        row.id === rowId
          ? {
              ...row,
              cells: updateCellValue(row.cells, colIdx, value),
            }
          : row
      )
    );
  };

  const updateColumn = (colIdx: number, value: string) => {
    setDraftCols(curr => curr.map((c, i) => (i === colIdx ? value : c)));
  };

  const addRow = (sectionId?: string) => {
    const next = [
      ...draftRows,
      {
        id: crypto.randomUUID(),
        cells: draftCols.map(() => ""),
        ...(sectionId ? { sectionId } : {}),
      },
    ];
    setDraftRows(next);
    commit(draftCols, next);
  };

  const addSection = () => {
    const nextSections = [
      ...draftSections,
      {
        id: crypto.randomUUID(),
        title: `Section ${draftSections.length + 1}`,
      },
    ];
    setDraftSections(nextSections);
    commit(draftCols, draftRows, nextSections);
  };

  const updateSectionTitle = (id: string, title: string, shouldCommit = false) => {
    const nextSections = draftSections.map(section =>
      section.id === id ? { ...section, title } : section
    );
    setDraftSections(nextSections);
    if (shouldCommit) {
      commit(draftCols, draftRows, nextSections);
    }
  };

  const addColumn = () => {
    const nextCols = [...draftCols, `Column ${draftCols.length + 1}`];
    const nextRows = draftRows.map(row => ({ ...row, cells: [...row.cells, ""] }));
    setDraftCols(nextCols);
    setDraftRows(nextRows);
    commit(nextCols, nextRows);
  };

  const hideRow = (rowId: string) => {
    const next = draftRows.map(row => (row.id === rowId ? { ...row, hidden: true } : row));
    setDraftRows(next);
    commit(draftCols, next);
  };

  const moveRow = (sourceRowId: string, targetRowId: string, placement: DropPlacement) => {
    if (sourceRowId === targetRowId) return;

    const sourceIndex = draftRows.findIndex(row => row.id === sourceRowId);
    const targetRow = draftRows.find(row => row.id === targetRowId);
    if (sourceIndex === -1 || !targetRow) return;

    const movingRow = {
      ...draftRows[sourceIndex],
      sectionId: targetRow.sectionId,
    };
    const remaining = draftRows.filter(row => row.id !== sourceRowId);
    const targetIndex = remaining.findIndex(row => row.id === targetRowId);
    if (targetIndex === -1) return;

    const insertIndex = placement === "after" ? targetIndex + 1 : targetIndex;
    const next = [...remaining];
    next.splice(insertIndex, 0, movingRow);
    setDraftRows(next);
    commit(draftCols, next);
  };

  const groups = buildTableGroups({
    rows: draftRows,
    sections: draftSections,
  });
  const columnSpan = draftCols.length + (isReadOnly ? 0 : 1);

  return (
    <TableWrapper>
      <Table>
        <thead>
          <tr>
            {draftCols.map((col, idx) => (
              <Th key={idx}>
                <ColInput
                  value={col}
                  readOnly={isReadOnly}
                  onChange={e => updateColumn(idx, e.target.value)}
                  onBlur={() => commit()}
                />
              </Th>
            ))}
            {!isReadOnly && (
              <Th>
                <AddColButton onClick={addColumn}>+</AddColButton>
              </Th>
            )}
          </tr>
        </thead>
        {groups.map(group => (
          <tbody key={group.section?.id ?? "unsectioned"}>
            {group.showHeader && (
              <SectionRow>
                <SectionCell colSpan={columnSpan}>
                  {group.section ? (
                    <SectionTitleInput
                      value={group.section.title}
                      readOnly={isReadOnly}
                      placeholder="Section"
                      onChange={e => updateSectionTitle(group.section!.id, e.target.value)}
                      onBlur={e =>
                        updateSectionTitle(group.section!.id, e.currentTarget.value, true)
                      }
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.currentTarget.blur();
                        }
                      }}
                    />
                  ) : (
                    <SectionTitle>Other</SectionTitle>
                  )}
                </SectionCell>
              </SectionRow>
            )}
            {group.rows.map((row, rowIdx) => (
              <TableBodyRow
                key={row.id}
                $dropPlacement={dropTargetRow?.rowId === row.id ? dropTargetRow.placement : null}
                onDragOver={event => {
                  if (isReadOnly || !draggedRowId || draggedRowId === row.id) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = "move";
                  setDropTargetRow({
                    rowId: row.id,
                    placement: getDropPlacement(event),
                  });
                }}
                onDragLeave={() => setDropTargetRow(null)}
                onDrop={event => {
                  event.preventDefault();
                  if (!draggedRowId) return;
                  const placement =
                    dropTargetRow?.rowId === row.id
                      ? dropTargetRow.placement
                      : getDropPlacement(event);
                  moveRow(draggedRowId, row.id, placement);
                  setDraggedRowId(null);
                  setDropTargetRow(null);
                }}
              >
                {draftCols.map((_, colIdx) => {
                  const cell = row.cells[colIdx] ?? "";
                  const isActive = activeCell?.rowId === row.id && activeCell?.col === colIdx;
                  const showRichView = !isActive && hasChips(cell);
                  const sourcesForCell =
                    colIdx === 0
                      ? getVisibleSourceIdsForRepeatedRun({
                          sourceIds: row.sources,
                          previousSourceIds: group.rows[rowIdx - 1]?.sources,
                        })
                      : [];
                  const sourcesEl =
                    sourcesForCell.length > 0 ? (
                      <InlineSources>
                        {sourcesForCell.map(noteId => (
                          <CitationChip key={noteId} noteId={noteId} />
                        ))}
                      </InlineSources>
                    ) : null;
                  if (isReadOnly || showRichView) {
                    return (
                      <Td
                        key={colIdx}
                        onClick={
                          isReadOnly
                            ? undefined
                            : () => setActiveCell({ rowId: row.id, col: colIdx })
                        }
                      >
                        <CellLine>
                          <CellValue>
                            {cell.length > 0 ? (
                              <ChippyText text={cell} inline />
                            ) : (
                              <CellPlaceholder />
                            )}
                          </CellValue>
                          {sourcesEl}
                        </CellLine>
                      </Td>
                    );
                  }
                  return (
                    <Td key={colIdx}>
                      <CellLine>
                        <CellInput
                          ref={isActive ? cellInputRef : undefined}
                          value={cell}
                          readOnly={isReadOnly}
                          onChange={e => updateCell(row.id, colIdx, e.target.value)}
                          onBlur={() => {
                            commitWithNotify(draftCols, draftRows);
                            if (isActive) setActiveCell(null);
                          }}
                        />
                        {sourcesEl}
                      </CellLine>
                    </Td>
                  );
                })}
                {!isReadOnly && (
                  <ActionTd>
                    <RowControlCluster>
                      <RowDragHandle
                        draggable
                        aria-label="Reorder row"
                        onDragStart={event => {
                          setDraggedRowId(row.id);
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData("text/plain", row.id);
                        }}
                        onDragEnd={() => {
                          setDraggedRowId(null);
                          setDropTargetRow(null);
                        }}
                      >
                        <MDSIcon kind={MDSIconKind.Grip} />
                      </RowDragHandle>
                      <TableRowMenu onHide={() => hideRow(row.id)} />
                    </RowControlCluster>
                  </ActionTd>
                )}
              </TableBodyRow>
            ))}
            {!isReadOnly && group.section && (
              <SectionRow>
                <SectionCell colSpan={columnSpan}>
                  <AddRowButton onClick={() => addRow(group.section!.id)}>+ Add row</AddRowButton>
                </SectionCell>
              </SectionRow>
            )}
          </tbody>
        ))}
      </Table>
      {!isReadOnly && (
        <FooterActions>
          <AddRowButton onClick={() => addRow()}>+ Add row</AddRowButton>
          <AddRowButton onClick={addSection}>+ Add section</AddRowButton>
        </FooterActions>
      )}
    </TableWrapper>
  );
};

const TableRowMenu: FC<{ onHide: () => void }> = ({ onHide }) => {
  return (
    <MDSMenuDropdown>
      <MDSIconButton
        slot={MDSMenuDropdownSlotName.Trigger}
        size={MDSIconButtonSize.XSmall}
        iconKind={MDSIconKind.More}
        variant={MDSIconButtonVariant.OnChrome}
        aria-label="Row actions"
        rounded
      />
      <MDSActionMenu aria-label="Row actions" slot={MDSMenuDropdownSlotName.Menu}>
        <MDSActionMenuItem
          id="hide-row"
          variant={MDSMenuItemVariant.OneLine}
          label="Hide row"
          icon={MDSIconKind.EyeSlash}
          onClick={onHide}
        />
      </MDSActionMenu>
    </MDSMenuDropdown>
  );
};

const buildTableContent = (columns: string[], rows: TableRow[], sections: WorkflowSection[]) => ({
  kind: "table" as const,
  columns,
  rows: rows.map(row => ({
    id: row.id,
    cells: row.cells,
    ...(row.sectionId ? { sectionId: row.sectionId } : {}),
    ...(row.hidden ? { hidden: true } : {}),
    ...(row.sources && row.sources.length > 0 ? { sources: row.sources } : {}),
  })),
  ...(sections.length > 0 ? { sections } : {}),
});

const normalizeRows = (rows: TableRowInput[], rowSources: string[][]): TableRow[] => {
  const seenIds = new Set<string>();
  return rows.map((row, index) => {
    const baseRow = Array.isArray(row)
      ? {
          id: `legacy-row-${index}`,
          cells: row.map(cell => String(cell)),
          sources: normalizeSources(rowSources[index]),
        }
      : {
          id: row.id || `row-${index}`,
          cells: row.cells.map(cell => String(cell)),
          sectionId: row.sectionId,
          hidden: row.hidden,
          sources: normalizeSources(row.sources ?? rowSources[index]),
        };
    const id = makeUniqueRowId(baseRow.id, seenIds);
    seenIds.add(id);
    return { ...baseRow, id };
  });
};

const normalizeSources = (sources: string[] | undefined): string[] | undefined => {
  if (!sources || sources.length === 0) return undefined;
  return sources;
};

const makeUniqueRowId = (id: string, seenIds: Set<string>): string => {
  if (!seenIds.has(id)) return id;
  let suffix = 2;
  while (seenIds.has(`${id}-${suffix}`)) {
    suffix += 1;
  }
  return `${id}-${suffix}`;
};

const buildTableGroups = ({
  rows,
  sections,
}: {
  rows: TableRow[];
  sections: WorkflowSection[];
}): TableGroup[] => {
  const sectionIds = new Set(sections.map(section => section.id));
  const visibleRows = rows.filter(row => !row.hidden);
  const sectionGroups = sections.map(section => ({
    section,
    rows: visibleRows.filter(row => row.sectionId === section.id),
    showHeader: true,
  }));
  const unsectionedRows = visibleRows.filter(
    row => !row.sectionId || !sectionIds.has(row.sectionId)
  );

  if (sections.length === 0) {
    return [
      {
        section: null,
        rows: unsectionedRows,
        showHeader: false,
      },
    ];
  }

  return [
    ...sectionGroups,
    ...(unsectionedRows.length > 0
      ? [
          {
            section: null,
            rows: unsectionedRows,
            showHeader: true,
          },
        ]
      : []),
  ];
};

const updateCellValue = (cells: string[], colIdx: number, value: string): string[] => {
  const next = [...cells];
  while (next.length <= colIdx) {
    next.push("");
  }
  next[colIdx] = value;
  return next;
};

const getDropPlacement = (event: DragEvent<HTMLElement>): DropPlacement => {
  const rect = event.currentTarget.getBoundingClientRect();
  return event.clientY > rect.top + rect.height / 2 ? "after" : "before";
};

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["2"]};
  overflow-x: auto;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th`
  text-align: left;
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.text.tertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const TableBodyRow = styled.tr<{ $dropPlacement: DropPlacement | null }>`
  box-shadow: ${({ $dropPlacement, theme }) => {
    if ($dropPlacement === "before") {
      return `inset 0 2px 0 ${theme.colors.border.emphasis}`;
    }
    if ($dropPlacement === "after") {
      return `inset 0 -2px 0 ${theme.colors.border.emphasis}`;
    }
    return "none";
  }};
`;

const Td = styled.td`
  padding: 8px 10px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  font-size: 14px;
  vertical-align: middle;
`;

const ActionTd = styled(Td)`
  width: 1%;
  white-space: nowrap;
`;

const SectionRow = styled.tr``;

const SectionCell = styled.td`
  padding: 8px 8px 4px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`;

const SectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`;

const SectionTitleInput = styled.input`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font: inherit;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  outline: none;
  padding: 0;
  text-transform: uppercase;
  width: 100%;
`;

const ColInput = styled.input`
  border: none;
  background: transparent;
  font-size: inherit;
  font-weight: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: inherit;
  outline: none;
  width: 100%;
  font-family: inherit;
`;

const CellInput = styled.input`
  border: none;
  background: transparent;
  font-size: inherit;
  color: inherit;
  outline: none;
  flex: 1 1 80px;
  min-width: 0;
  font-family: inherit;
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 4px;
  &:focus {
    background: ${({ theme }) => theme.colors.background.button.onChrome.hover};
  }
`;

const CellLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const CellValue = styled.div`
  flex: 1 1 80px;
  min-width: 0;
`;

const InlineSources = styled.div`
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  flex: 0 1 auto;
  gap: 4px;
  max-width: min(180px, 50%);
  min-width: 0;
`;

const FooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["3"]};
`;

const AddRowButton = styled.button`
  background: transparent;
  border: none;
  text-align: left;
  padding: 4px 0;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: 13px;
  font-family: inherit;
  align-self: flex-start;
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const CellPlaceholder = styled.span`
  display: inline-block;
  min-width: 24px;
  min-height: 1em;
`;

const AddColButton = styled.button`
  background: transparent;
  border: 1px dashed ${({ theme }) => theme.colors.border.default};
  border-radius: 4px;
  padding: 2px 8px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-family: inherit;
  &:hover {
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const RowControlCluster = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const RowDragHandle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;
